import { Typography } from "@mui/material";
import { CollapsibleAlert } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { formatPercentage } from "Infrastructure/Utils/NumberUtils";

type Props = {
  readonly feeRate: number;
  readonly inflationRate: number;
  readonly interestRate: number;
  readonly pensionInterestRate: number;
  readonly targetAmount: number;
  readonly monthlyContribution: number;
  readonly yearsRemaining: number;
  readonly pensionAmount: number;
  readonly pensionDuration: number;
  readonly infinitePension: boolean;
};

export const PensionGoalModelingHint: React.FC<Props> = ({
  feeRate,
  inflationRate,
  interestRate,
  pensionInterestRate,
  targetAmount,
  monthlyContribution,
  yearsRemaining,
  pensionAmount,
  pensionDuration,
  infinitePension,
}) => {
  const { t } = useResource();

  return (
    <CollapsibleAlert title={t(Resources.AnalysisDetail.Goals.PensionHint.Title)}>
      {t(Resources.AnalysisDetail.Goals.PensionHint.FirstPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatCurrency(targetAmount, CurrencyCode.CZK)}
      </Typography>
      . {t(Resources.AnalysisDetail.Goals.PensionHint.SecondPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatPercentage(interestRate || 0)}*
      </Typography>{" "}
      {t(Resources.AnalysisDetail.Goals.PensionHint.ThirdPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatCurrency(monthlyContribution, CurrencyCode.CZK)}**
      </Typography>{" "}
      {t(Resources.AnalysisDetail.Goals.PensionHint.ForDuration)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {yearsRemaining} {t(Resources.Common.Years)}
      </Typography>
      . {t(Resources.AnalysisDetail.Goals.PensionHint.RentPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatCurrency(pensionAmount, CurrencyCode.CZK)}
      </Typography>{" "}
      {t(Resources.AnalysisDetail.Goals.PensionHint.Monthly)}
      {/* An infinite pension has no drawdown period; the duration field only keeps a placeholder default then. */}
      {!infinitePension && (
        <>
          {" "}
          {t(Resources.AnalysisDetail.Goals.PensionHint.ForDuration)}{" "}
          <Typography display="inline" variant="body2" fontWeight={500}>
            {pensionDuration} {t(Resources.Common.Years)}
          </Typography>
        </>
      )}
      ***.
      <br />
      <br />* {t(Resources.AnalysisDetail.Goals.PensionHint.FourthPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatPercentage(inflationRate || 0)}
      </Typography>
      {t(Resources.AnalysisDetail.Goals.PensionHint.FifthPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatPercentage((interestRate || 0) - (inflationRate || 0))}
      </Typography>
      .
      <br />
      ** {t(Resources.AnalysisDetail.Goals.PensionHint.SixthPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatPercentage(feeRate || 0)}
      </Typography>{" "}
      {t(Resources.AnalysisDetail.Goals.PensionHint.SeventhPhrase)}.
      <br />
      *** {t(Resources.AnalysisDetail.Goals.PensionHint.DrawdownFootnoteFirstPhrase)}{" "}
      <Typography display="inline" variant="body2" fontWeight={500}>
        {formatPercentage(pensionInterestRate || 0)}
      </Typography>
      {t(Resources.AnalysisDetail.Goals.PensionHint.DrawdownFootnoteSecondPhrase)}
    </CollapsibleAlert>
  );
};
